import { useEffect, useState } from 'react';
import type { Catalog } from '@tales/story-schema';
import { readJson, writeJson } from '../lib/storage';

interface NodeStats {
  id: string;
  starts: number;
  completes: number;
  completionRate: number | null;
  avgGapMs: number | null;
  choiceShown: number;
  chosen: number;
  timeouts: number;
  avgDecisionMs: number | null;
  choices: { id: string; count: number; share: number | null }[];
}
interface EpisodeStats {
  episode: Record<string, number | null | { id: string; count: number }[]> & { endingDistribution: { id: string; count: number }[] };
  nodes: NodeStats[];
}

const pct = (v: number | null | undefined) => (v === null || v === undefined ? '–' : `${Math.round(v * 100)}%`);

/** Internal funnel dashboard: /admin — needs the API admin token (stored locally). */
export function Admin({ catalog }: { catalog: Catalog }) {
  const [token, setToken] = useState(() => readJson<string>('tales.admin.token', ''));
  const [sel, setSel] = useState<{ s: string; e: string } | null>(null);
  const [data, setData] = useState<EpisodeStats | null>(null);
  const [overview, setOverview] = useState<Record<string, unknown>[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    writeJson('tales.admin.token', token);
    fetch('/api/v1/admin/overview', { headers: { 'x-admin-token': token } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        setOverview(((await r.json()) as { episodes: Record<string, unknown>[] }).episodes);
        setErr(null);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [token]);

  useEffect(() => {
    if (!token || !sel) return;
    fetch(`/api/v1/admin/stats/${sel.s}/${sel.e}`, { headers: { 'x-admin-token': token } })
      .then(async (r) => setData((await r.json()) as EpisodeStats))
      .catch(() => setData(null));
  }, [token, sel]);

  return (
    <div className="home" style={{ padding: '20px 16px' }}>
      <h1 style={{ fontSize: 22 }}>Tales · funnel</h1>
      <p style={{ color: 'var(--muted)', fontSize: 13 }}>Internal. Counters are aggregated live from player events; raw events are in the event lake.</p>
      <input className="admin-input" placeholder="admin token" value={token} onChange={(e) => setToken(e.target.value)} />
      {err ? <p style={{ color: 'var(--accent)' }}>API: {err} (wrong token or API offline)</p> : null}
      {overview ? (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Episode</th>
              <th>Price</th>
              <th>Sessions</th>
              <th>Plays</th>
              <th>Engaged</th>
              <th>Endings</th>
              <th>Gate</th>
              <th>Intent users</th>
              <th>Unlocks</th>
            </tr>
          </thead>
          <tbody>
            {overview.map((r) => (
              <tr key={`${String(r['seriesId'])}/${String(r['episodeId'])}`} onClick={() => setSel({ s: String(r['seriesId']), e: String(r['episodeId']) })} style={{ cursor: 'pointer' }}>
                <td>{String(r['title'])}</td>
                <td>{String(r['price'])}</td>
                <td>{Number(r['sessions'] ?? 0)}</td>
                <td>{Number(r['playStarts'] ?? 0)}</td>
                <td>{Number(r['engagedUsers'] ?? 0)}</td>
                <td>{Number(r['endings'] ?? 0)}</td>
                <td>{Number(r['unlockGateShown'] ?? 0)}</td>
                <td>{Number(r['unlockIntentUsers'] ?? 0)}</td>
                <td>{Number(r['unlocks'] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {sel && data ? (
        <section style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 18 }}>
            {catalog.series.find((s) => s.id === sel.s)?.title} · {sel.e}
          </h2>
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>
            completion {pct(data.episode['completionRate'] as number | null)} · intent rate {pct(data.episode['intentRate'] as number | null)} · endings:{' '}
            {data.episode.endingDistribution.map((e) => `${e.id} ${e.count}`).join(', ') || '–'}
          </p>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Node</th>
                <th>Starts</th>
                <th>Completed</th>
                <th>Gap</th>
                <th>Decision</th>
                <th>Timeouts</th>
                <th>Split</th>
              </tr>
            </thead>
            <tbody>
              {data.nodes
                .sort((a, b) => a.id.localeCompare(b.id))
                .map((n) => (
                  <tr key={n.id}>
                    <td>{n.id}</td>
                    <td>{n.starts}</td>
                    <td>{pct(n.completionRate)}</td>
                    <td>{n.avgGapMs ?? '–'}ms</td>
                    <td>{n.avgDecisionMs ? `${(n.avgDecisionMs / 1000).toFixed(1)}s` : '–'}</td>
                    <td>{n.timeouts || '–'}</td>
                    <td>{n.choices.map((c) => `${c.id} ${pct(c.share)}`).join(' · ') || '–'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  );
}
